import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_ROUTES, API_DOMAIN, headers } from '../../../Reusable_Components/Config/apiConfig';
import { ToastContext } from '../../../../context/ToastContext';
import { useFormDraft } from '../../../../hooks/useFormDraft';
import { fmtDateTime } from '../../../../utils/fmt';
import * as listCache from '../../../../services/listCache';
import DatePicker from '../../../Reusable_Components/Template_Components/DatePicker';
import AsyncSelect from '../../../Reusable_Components/Template_Components/AsyncSelect';

// ── Constants ─────────────────────────────────────────────────────────────────

const CUSTOMER_TYPES = [
  { value: 'Individual', label: 'Individual', icon: '👤', desc: 'A single person' },
  { value: 'Business',   label: 'Business',   icon: '🏢', desc: 'SME or startup' },
  { value: 'Enterprise', label: 'Enterprise', icon: '🏛', desc: 'Large organisation' },
  { value: 'Partner',    label: 'Partner',    icon: '🤝', desc: 'Reseller / partner' },
];

const STATUS_OPTIONS = [
  { value: 'Prospect',  label: 'Prospect',  color: '#f59e0b' },
  { value: 'Active',    label: 'Active',    color: '#22c55e' },
  { value: 'Inactive',  label: 'Inactive',  color: '#94a3b8' },
  { value: 'At Risk',   label: 'At Risk',   color: '#ef4444' },
  { value: 'Former',    label: 'Former',    color: '#64748b' },
  { value: 'VIP',       label: 'VIP',       color: '#8b5cf6' },
];

const INDUSTRIES = [
  'Agriculture', 'Banking & Finance', 'Construction', 'Education',
  'Energy & Utilities', 'Government', 'Healthcare', 'Hospitality & Tourism',
  'ICT & Technology', 'Insurance', 'Legal & Professional Services',
  'Logistics & Transport', 'Manufacturing', 'Media & Entertainment',
  'NGO / Non-profit', 'Real Estate', 'Retail & E-commerce',
  'Telecommunications', 'Other',
];

const CONTACT_PREF = ['Email', 'Phone', 'WhatsApp', 'In-Person', 'Video Call'];

const TIER_CONFIG = {
  Platinum: { bg: '#f5f3ff', fg: '#6d28d9', bar: '#8b5cf6' },
  Gold:     { bg: '#fffbeb', fg: '#92400e', bar: '#f59e0b' },
  Silver:   { bg: '#f8fafc', fg: '#475569', bar: '#94a3b8' },
  Bronze:   { bg: '#fff7ed', fg: '#9a3412', bar: '#f97316' },
  Standard: { bg: '#f0fdf4', fg: '#166534', bar: '#22c55e' },
};

const EMPTY = {
  name: '',
  email: '',
  phone: '',
  secondary_phone: '',
  company: '',
  industry: '',
  website: '',
  address: '',
  city: '',
  country: 'Kenya',
  postal_code: '',
  customer_type: 'Business',
  status: 'Active',
  tier: 'Standard',
  preferred_contact: 'Email',
  lead_source: '',
  account_manager: '',
  customer_since: '',
  annual_revenue: '',
  employee_count: '',
  notes: '',
};

const SOURCE_OPTIONS = [
  'Website', 'Referral', 'Cold Call', 'Email Campaign',
  'LinkedIn', 'Trade Show / Event', 'Partner / Reseller', 'Walk-in', 'Other',
];

// ── Sub-components ────────────────────────────────────────────────────────────

const Field = ({ label, required, children, wide }) => (
  <div className={`form-field${wide ? ' span-full' : ''}`}>
    <label className="form-label">{label}{required && <span className="req"> *</span>}</label>
    {children}
  </div>
);

const SectionHeader = ({ icon, title, subtitle }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 10,
    marginBottom: 20, paddingBottom: 14,
    borderBottom: '1px solid var(--border)',
  }}>
    <div style={{
      width: 36, height: 36, borderRadius: 8,
      background: 'var(--color-primary-pale)', color: 'var(--color-primary)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
    }}>
      {icon}
    </div>
    <div>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-main)' }}>{title}</div>
      {subtitle && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{subtitle}</div>}
    </div>
  </div>
);

const TypeSelector = ({ value, onChange }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
    {CUSTOMER_TYPES.map(t => {
      const active = value === t.value;
      return (
        <button
          key={t.value}
          type="button"
          onClick={() => onChange(t.value)}
          style={{
            padding: '10px 6px', borderRadius: 8, textAlign: 'center',
            border: active ? '2px solid var(--color-primary)' : '2px solid var(--border)',
            background: active ? 'var(--color-primary-pale)' : 'transparent',
            color: active ? 'var(--color-primary)' : 'var(--text-muted)',
            cursor: 'pointer', transition: 'all 0.13s',
          }}
        >
          <div style={{ fontSize: 20, marginBottom: 4 }}>{t.icon}</div>
          <div style={{ fontSize: 11, fontWeight: 700 }}>{t.label}</div>
          <div style={{ fontSize: 10, marginTop: 2, color: 'inherit', opacity: 0.75 }}>{t.desc}</div>
        </button>
      );
    })}
  </div>
);

const StatusDot = ({ color }) => (
  <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block', marginRight: 6, flexShrink: 0 }} />
);

// ── Main Component ────────────────────────────────────────────────────────────

const CustomerInput = () => {
  const { customerId = '' } = useParams();
  const navigate = useNavigate();
  const toast = useContext(ToastContext);

  const decodedId = customerId ? atob(customerId) : '';
  const id = decodedId.replace(/\D/g, '');
  const isEditing = !!id;

  const [form, setForm] = useState(EMPTY);
  const [original, setOriginal] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [draftBanner, setDraftBanner] = useState(null);

  const { saveDraft, loadDraft, clearDraft } = useFormDraft('customer', id || null);

  // On create-mode mount, check for a saved draft
  useEffect(() => {
    if (customerId) return;
    const draft = loadDraft();
    if (draft?.data && Object.keys(draft.data).length > 0) {
      setDraftBanner(draft);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch existing record in edit mode
  useEffect(() => {
    if (!customerId) return;
    axios.get(`${API_DOMAIN}${API_ROUTES.customer}${id}/`, { headers })
      .then(res => { setForm(prev => ({ ...prev, ...res.data })); setOriginal(res.data); })
      .catch(err => console.error('Customer fetch error:', err));
  }, [customerId, id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Central updater — persists draft on every change
  const updateForm = useCallback((updater) => {
    setForm(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      saveDraft(next);
      return next;
    });
  }, [saveDraft]);

  const set = (field) => (e) => updateForm(prev => ({ ...prev, [field]: e.target ? e.target.value : e }));

  const formatDraftTime = (iso) => fmtDateTime(iso);

  const handleRestoreDraft = () => { setForm(draftBanner.data); setDraftBanner(null); };
  const handleDiscardDraft = () => { clearDraft(); setDraftBanner(null); setForm(EMPTY); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const url = isEditing
        ? `${API_DOMAIN}${API_ROUTES.customer}${id}/`
        : `${API_DOMAIN}${API_ROUTES.customer}`;
      const method = isEditing ? axios.put : axios.post;
      // DRF's DateField/DecimalField reject '' outright ("wrong format") — an
      // empty optional field must be sent as null, not the input's raw ''.
      const payload = {
        ...form,
        customer_since: form.customer_since || null,
        annual_revenue: form.annual_revenue === '' ? null : form.annual_revenue,
      };
      await method(url, payload, { headers });
      clearDraft();
      listCache.invalidate((u) => u.startsWith(`${API_DOMAIN}${API_ROUTES.customer}`));
      toast?.addToast({ type: 'success', message: isEditing ? 'Customer updated.' : 'Customer created.' });
      navigate('/customer/list');
    } catch (err) {
      const data = err?.response?.data;
      const msg = data
        ? typeof data === 'string' ? data
          : Object.entries(data).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join(' · ')
        : 'Failed to save. Please check your inputs.';
      toast?.addToast({ type: 'error', message: msg, duration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  const currentStatus = STATUS_OPTIONS.find(s => s.value === form.status) || STATUS_OPTIONS[1];
  const currentType   = CUSTOMER_TYPES.find(t => t.value === form.customer_type) || CUSTOMER_TYPES[1];
  const tierCfg       = TIER_CONFIG[form.tier] || TIER_CONFIG.Standard;
  const initials      = form.name
    ? form.name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()
    : '?';

  return (
    <div>
      {/* ── Page Header ── */}
      <div className="page-header">
        <div className="page-header-left">
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-muted)', marginBottom: 6 }}>
            <Link to="/customer/list" style={{ color: 'var(--color-primary)' }}>Customers</Link>
            <span>›</span>
            <span>{isEditing ? 'Edit Customer' : 'New Customer'}</span>
          </div>
          <h1 className="page-title">{isEditing ? 'Edit Customer Record' : 'Add New Customer'}</h1>
          <p className="page-subtitle">Build a complete profile to track this customer's journey</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/customer/list')}>
            Cancel
          </button>
        </div>
      </div>

      <form id="customer-form" onSubmit={handleSubmit}>
        {draftBanner && (
          <div className="form-draft-banner">
            <span className="form-draft-banner-icon">⚠</span>
            <span className="form-draft-banner-text">
              Unsaved draft found
              <span>— saved {formatDraftTime(draftBanner.savedAt)}</span>
            </span>
            <div className="form-draft-banner-actions">
              <button type="button" className="form-draft-btn-restore" onClick={handleRestoreDraft}>Restore</button>
              <button type="button" className="form-draft-btn-discard" onClick={handleDiscardDraft}>Discard</button>
            </div>
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 20, alignItems: 'start' }}>

          {/* ── Left column ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* Section 1: Identity */}
            <div className="card">
              <div className="card-body">
                <SectionHeader icon="🆔" title="Customer Identity" subtitle="Who is this customer and what type of account are they?" />
                <div className="form-grid" style={{ marginBottom: 20 }}>
                  <Field label="Full Name / Business Name" required wide>
                    <input
                      className="form-input"
                      placeholder="e.g. Jane Wanjiku or Acme Holdings Ltd"
                      value={form.name}
                      onChange={set('name')}
                      required
                    />
                  </Field>
                  <Field label="Status">
                    <select className="form-select" value={form.status} onChange={set('status')}>
                      {STATUS_OPTIONS.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Account Tier">
                    <select className="form-select" value={form.tier} onChange={set('tier')}>
                      {Object.keys(TIER_CONFIG).map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </Field>
                </div>
                <Field label="Customer Type" required>
                  <TypeSelector value={form.customer_type} onChange={val => updateForm(p => ({ ...p, customer_type: val }))} />
                </Field>
              </div>
            </div>

            {/* Section 2: Contact Details */}
            <div className="card">
              <div className="card-body">
                <SectionHeader icon="📞" title="Contact Details" subtitle="How to reach this customer" />
                <div className="form-grid">
                  <Field label="Email Address" required>
                    <input
                      className="form-input"
                      type="email"
                      placeholder="jane@example.com"
                      value={form.email}
                      onChange={set('email')}
                      required
                    />
                  </Field>
                  <Field label="Primary Phone">
                    <input
                      className="form-input"
                      type="tel"
                      placeholder="+254 700 000 000"
                      value={form.phone}
                      onChange={set('phone')}
                    />
                  </Field>
                  <Field label="Secondary Phone / WhatsApp">
                    <input
                      className="form-input"
                      type="tel"
                      placeholder="+254 700 000 001"
                      value={form.secondary_phone}
                      onChange={set('secondary_phone')}
                    />
                  </Field>
                  <Field label="Preferred Contact Method">
                    <select className="form-select" value={form.preferred_contact} onChange={set('preferred_contact')}>
                      {CONTACT_PREF.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </Field>
                </div>
              </div>
            </div>

            {/* Section 3: Business Information */}
            <div className="card">
              <div className="card-body">
                <SectionHeader icon="🏢" title="Business Information" subtitle="Organisation details and online presence" />
                <div className="form-grid">
                  <Field label="Company / Organisation">
                    <input
                      className="form-input"
                      placeholder="e.g. Acme Holdings Ltd"
                      value={form.company}
                      onChange={set('company')}
                    />
                  </Field>
                  <Field label="Industry">
                    <select className="form-select" value={form.industry} onChange={set('industry')}>
                      <option value="">— Select industry —</option>
                      {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                    </select>
                  </Field>
                  <Field label="Website">
                    <input
                      className="form-input"
                      type="url"
                      placeholder="https://example.com"
                      value={form.website}
                      onChange={set('website')}
                    />
                  </Field>
                  <Field label="Annual Revenue (KES)">
                    <input
                      className="form-input"
                      type="number"
                      min="0"
                      step="100000"
                      placeholder="0"
                      value={form.annual_revenue}
                      onChange={set('annual_revenue')}
                    />
                  </Field>
                  <Field label="No. of Employees">
                    <select className="form-select" value={form.employee_count} onChange={set('employee_count')}>
                      <option value="">— Select range —</option>
                      {[
                        { value: '1-10',     label: '1–10' },
                        { value: '11-50',    label: '11–50' },
                        { value: '51-200',   label: '51–200' },
                        { value: '201-500',  label: '201–500' },
                        { value: '501-1000', label: '501–1,000' },
                        { value: '1000+',    label: '1,000+' },
                      ].map(r => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Lead Source">
                    <select className="form-select" value={form.lead_source} onChange={set('lead_source')}>
                      <option value="">— Select source —</option>
                      {SOURCE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                </div>
              </div>
            </div>

            {/* Section 4: Location */}
            <div className="card">
              <div className="card-body">
                <SectionHeader icon="📍" title="Location" subtitle="Physical address and region" />
                <div className="form-grid">
                  <Field label="Street Address" wide>
                    <textarea
                      className="form-textarea"
                      rows={2}
                      placeholder="Building, street, estate…"
                      value={form.address}
                      onChange={set('address')}
                    />
                  </Field>
                  <Field label="City / Town">
                    <input
                      className="form-input"
                      placeholder="e.g. Nairobi"
                      value={form.city}
                      onChange={set('city')}
                    />
                  </Field>
                  <Field label="Country">
                    <input
                      className="form-input"
                      placeholder="e.g. Kenya"
                      value={form.country}
                      onChange={set('country')}
                    />
                  </Field>
                  <Field label="Postal / ZIP Code">
                    <input
                      className="form-input"
                      placeholder="00100"
                      value={form.postal_code}
                      onChange={set('postal_code')}
                    />
                  </Field>
                </div>
              </div>
            </div>

            {/* Section 5: Relationship */}
            <div className="card">
              <div className="card-body">
                <SectionHeader icon="🤝" title="Relationship" subtitle="Account ownership, origin, and internal notes" />
                <div className="form-grid">
                  <div className="form-field">
                    <AsyncSelect
                      label="Account Manager"
                      endpoint={API_ROUTES.users}
                      getOptionLabel={(u) => u.username || u.email || `User ${u.id}`}
                      value={form.account_manager}
                      onChange={set('account_manager')}
                    />
                  </div>
                  <Field label="Customer Since">
                    <DatePicker
                      value={form.customer_since}
                      onChange={set('customer_since')}
                      placeholder="Select date"
                      dateConstraint="past"
                    />
                  </Field>
                  <Field label="Internal Notes" wide>
                    <textarea
                      className="form-textarea"
                      rows={4}
                      placeholder="Background, special requirements, history, or any context about this customer…"
                      value={form.notes}
                      onChange={set('notes')}
                    />
                  </Field>
                </div>
              </div>
            </div>

          </div>

          {/* ── Right sidebar ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Avatar / identity preview */}
            <div className="card">
              <div className="card-body" style={{ textAlign: 'center' }}>
                <div style={{
                  width: 64, height: 64, borderRadius: '50%',
                  background: 'var(--color-primary-pale)',
                  color: 'var(--color-primary)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 22, fontWeight: 800,
                  margin: '0 auto 12px',
                  letterSpacing: 1,
                }}>
                  {initials}
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-main)', marginBottom: 4 }}>
                  {form.name || 'Customer Name'}
                </div>
                {form.company && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>{form.company}</div>
                )}
                <div style={{
                  display: 'inline-flex', alignItems: 'center',
                  padding: '3px 12px', borderRadius: 99,
                  background: currentStatus.color + '18',
                  border: `1px solid ${currentStatus.color}40`,
                  fontSize: 11, fontWeight: 600, color: currentStatus.color,
                  gap: 5,
                }}>
                  <StatusDot color={currentStatus.color} />
                  {currentStatus.label}
                </div>
                <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)' }}>
                  {currentType.icon} {currentType.label}
                </div>
              </div>
            </div>

            {/* Account Tier */}
            <div className="card">
              <div className="card-header">
                <span className="card-title">Account Tier</span>
              </div>
              <div className="card-body">
                <div style={{
                  padding: '10px 14px', borderRadius: 8,
                  background: tierCfg.bg, border: `1px solid ${tierCfg.bar}40`,
                  display: 'flex', alignItems: 'center', gap: 10,
                }}>
                  <div style={{
                    width: 10, height: 10, borderRadius: '50%',
                    background: tierCfg.bar, flexShrink: 0,
                  }} />
                  <span style={{ fontSize: 14, fontWeight: 700, color: tierCfg.fg }}>{form.tier}</span>
                </div>
                <div style={{ display: 'flex', gap: 4, marginTop: 10, flexWrap: 'wrap' }}>
                  {Object.keys(TIER_CONFIG).map(t => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => updateForm(p => ({ ...p, tier: t }))}
                      style={{
                        flex: 1, minWidth: 58, padding: '5px 0', fontSize: 10, fontWeight: 600,
                        borderRadius: 6, border: `1px solid ${form.tier === t ? TIER_CONFIG[t].bar : 'var(--border)'}`,
                        background: form.tier === t ? TIER_CONFIG[t].bg : 'transparent',
                        color: form.tier === t ? TIER_CONFIG[t].fg : 'var(--text-muted)',
                        cursor: 'pointer',
                      }}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Quick info */}
            <div className="card">
              <div className="card-header">
                <span className="card-title">Quick Info</span>
              </div>
              <div className="card-body">
                {[
                  { label: 'Email',   value: form.email   || '—' },
                  { label: 'Phone',   value: form.phone   || '—' },
                  { label: 'City',    value: form.city    || '—' },
                  { label: 'Industry',value: form.industry|| '—' },
                ].map(({ label, value }) => (
                  <div key={label} style={{
                    display: 'flex', justifyContent: 'space-between', gap: 8,
                    padding: '6px 0', borderBottom: '1px solid var(--border)',
                    fontSize: 12,
                  }}>
                    <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}>{label}</span>
                    <span style={{
                      color: 'var(--text-main)', fontWeight: 500,
                      textAlign: 'right', overflow: 'hidden',
                      textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140,
                    }}>{value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Tips */}
            <div className="card">
              <div className="card-header">
                <span className="card-title">Tips</span>
              </div>
              <div className="card-body">
                {[
                  { icon: '🔗', text: 'Link interactions and notes to this customer after saving' },
                  { icon: '📊', text: 'Set the tier to help prioritise high-value accounts' },
                  { icon: '📅', text: 'Record the customer-since date for accurate tenure tracking' },
                ].map(({ icon, text }, i) => (
                  <div key={i} style={{
                    display: 'flex', gap: 10, fontSize: 12, color: 'var(--text-sub)',
                    marginBottom: i < 2 ? 10 : 0,
                  }}>
                    <span>{icon}</span>
                    <span>{text}</span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>

        {/* ── Form actions ── */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 8 }}>
          <button type="button" className="btn btn-ghost" onClick={() => { clearDraft(); setForm(isEditing ? original : EMPTY); }}>
            Reset
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving} style={{ minWidth: 150 }}>
            {saving
              ? <><span className="spinner" style={{ width: 13, height: 13, borderWidth: 2 }} /> Saving…</>
              : isEditing ? '✓ Update Customer' : '+ Create Customer'
            }
          </button>
        </div>
      </form>
    </div>
  );
};

export default CustomerInput;
